import {
  type JsonValue,
  type NodeParam,
  type NodeStruct,
  type RepeatAttemptPin,
  type RepeatBodySlot,
  type RepeatControlResult,
  type RepeatExecutionConfig,
  type RepeatJudgePin,
  type RepeatPseudoCycleConfig,
  type RepeatProgress,
} from '@/types/node-types';
import type { NodeStatusEvent } from '@/types/workflow-types';

export const REPEAT_NODE_ID = 'repeat_until_node';
export const REPEAT_CAPABILITY = 'workflow.repeat_until.v1';
export const REPEAT_REQUIRED_CAPABILITIES = [
  REPEAT_CAPABILITY,
  'workflow.repeat_until.activity_dispatch.v1',
  'workflow.repeat_until.persistence.v1',
  'workflow.repeat_until.exit_expressions.v1',
] as const;
export const REPEAT_MAX_ITERATIONS_HARD = 10;
export const REPEAT_DEFAULT_MAX_ITERATIONS = 3;
export const REPEAT_SOURCE_HANDLE = 'repeat-until:repeat';
export const REPEAT_FEEDBACK_HANDLE_PREFIX = 'repeat-until:feedback:';
export const REPEAT_CONDITION_ROLE = 'repeat_condition';
export const REPEAT_BACK_EDGE_ROLE = 'repeat_feedback';

export type RepeatSlotRole = 'attempt' | 'judge';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isBodySlot = (value: unknown): value is RepeatBodySlot =>
  isRecord(value) &&
  (value.kind === 'input' || value.kind === 'param') &&
  typeof value.target_id === 'string' &&
  value.target_id.length > 0;

const isSlotPin = (value: unknown): boolean =>
  isRecord(value) &&
  value.kind === 'call' &&
  [
    'semantic_child_id',
    'catalog_node_id',
    'node_version',
    'node_contract_version',
    'capability_identity',
    'catalog_contract_digest',
  ].every((key) => typeof value[key] === 'string') &&
  (value.iteration_slot === null || isBodySlot(value.iteration_slot)) &&
  isRecord(value.static_params) &&
  isRecord(value.node_template);

const isAttemptPin = (value: unknown): boolean =>
  isSlotPin(value) &&
  ((value as Record<string, unknown>).input_slot === null ||
    isBodySlot((value as Record<string, unknown>).input_slot));

const isJudgePin = (value: unknown): boolean =>
  isSlotPin(value) &&
  isBodySlot((value as Record<string, unknown>).subject_slot);

const hasAllRepeatCapabilities = (capabilities: unknown): boolean => {
  if (!Array.isArray(capabilities)) return false;
  const values = new Set(
    capabilities.filter((value): value is string => typeof value === 'string'),
  );
  return REPEAT_REQUIRED_CAPABILITIES.every((capability) =>
    values.has(capability),
  );
};

export const isRepeatNode = (struct: NodeStruct | undefined): boolean =>
  struct?.node_id === REPEAT_NODE_ID;

export const isExecutableRepeatCarrier = (struct: NodeStruct): boolean =>
  struct.node_id === REPEAT_NODE_ID &&
  struct.capability_identity === REPEAT_CAPABILITY &&
  struct.contract_version === '1.0' &&
  struct.runtime_special_case === 'temporal_repeat_until_v1' &&
  hasAllRepeatCapabilities(struct.required_capabilities);

const roleEligible = (struct: NodeStruct, role: RepeatSlotRole): boolean => {
  const contract = struct.repeat_body_contract;
  const supported =
    role === 'attempt'
      ? struct.repeat_attempt_supported
      : struct.repeat_judge_supported;
  return (
    struct.node_id !== REPEAT_NODE_ID &&
    supported === true &&
    Boolean(struct.contract_version) &&
    Boolean(struct.capability_identity) &&
    Boolean(struct.repeat_catalog_digest) &&
    hasAllRepeatCapabilities(struct.repeat_required_runtime_capabilities) &&
    contract?.version === '1.0' &&
    contract.roles.includes(role) &&
    contract.supports_files === false &&
    contract.supports_handles === false &&
    contract.supports_clarification === false &&
    (role === 'attempt' || contract.subject_slots.length > 0)
  );
};

export const isRepeatAttemptEligible = (struct: NodeStruct): boolean =>
  roleEligible(struct, 'attempt');

export const isRepeatJudgeEligible = (struct: NodeStruct): boolean =>
  roleEligible(struct, 'judge');

export const sanitizeRepeatCatalog = (catalog: NodeStruct[]): NodeStruct[] =>
  catalog
    .filter(
      (node) =>
        node.node_id !== REPEAT_NODE_ID || isExecutableRepeatCarrier(node),
    )
    .map((node) => {
      if (
        node.node_id === REPEAT_NODE_ID ||
        (!node.repeat_attempt_supported && !node.repeat_judge_supported)
      ) {
        return node;
      }
      const attempt = node.repeat_attempt_supported
        ? isRepeatAttemptEligible(node)
        : false;
      const judge = node.repeat_judge_supported
        ? isRepeatJudgeEligible(node)
        : false;
      if (
        attempt === Boolean(node.repeat_attempt_supported) &&
        judge === Boolean(node.repeat_judge_supported)
      ) {
        return node;
      }
      return {
        ...node,
        repeat_attempt_supported: attempt,
        repeat_judge_supported: judge,
      };
    });

export const defaultRepeatPseudoCycleConfig = (
  instanceId: string,
): RepeatPseudoCycleConfig => ({
  version: '2.0',
  semantic_id: `repeat:${instanceId}`,
  max_iterations: REPEAT_DEFAULT_MAX_ITERATIONS,
  exit_condition: {
    op: 'eq',
    left: { op: 'path', path: ['judge', 'passed'] },
    right: { op: 'literal', value: true },
  },
  feedback_source_path: ['judge', 'feedback'],
  feedback_bindings: [],
  emit_mode: 'last_attempt',
  region: null,
  runtime_limits: null,
});

export const getRepeatConfig = (
  struct: NodeStruct | undefined,
): RepeatExecutionConfig | null => {
  if (!struct || !isRepeatNode(struct)) return null;
  const value = struct.params.find(
    (param) => param.field_id === 'repeat_config',
  )?.value;
  if (!isRecord(value) || value.version !== '1.0') return null;
  if (
    typeof value.semantic_id !== 'string' ||
    typeof value.max_iterations !== 'number' ||
    value.emit_mode !== 'last_attempt' ||
    !Array.isArray(value.feedback_bindings) ||
    (value.exit_condition !== null && !isRecord(value.exit_condition)) ||
    (value.attempt !== null && !isAttemptPin(value.attempt)) ||
    (value.judge !== null && !isJudgePin(value.judge))
  ) {
    return null;
  }
  return value as unknown as RepeatExecutionConfig;
};

export const getRepeatPseudoCycleConfig = (
  struct: NodeStruct | undefined,
): RepeatPseudoCycleConfig | null => {
  if (!struct || !isRepeatNode(struct)) return null;
  const value = struct.params.find(
    (param) => param.field_id === 'repeat_config',
  )?.value;
  if (
    !isRecord(value) ||
    (value.version !== '2.0' && value.version !== '2.1') ||
    typeof value.semantic_id !== 'string' ||
    typeof value.max_iterations !== 'number' ||
    value.emit_mode !== 'last_attempt' ||
    !isRecord(value.exit_condition) ||
    !Array.isArray(value.feedback_source_path) ||
    !Array.isArray(value.feedback_bindings)
  ) {
    return null;
  }
  return value as unknown as RepeatPseudoCycleConfig;
};

export const setRepeatPseudoCycleConfig = (
  struct: NodeStruct,
  config: RepeatPseudoCycleConfig,
): NodeStruct => ({
  ...struct,
  params: struct.params.map((param) =>
    param.field_id === 'repeat_config'
      ? { ...param, value: config as unknown as JsonValue }
      : param,
  ),
});

export const setRepeatConfig = (
  struct: NodeStruct,
  config: RepeatExecutionConfig,
): NodeStruct => ({
  ...struct,
  params: struct.params.map((param) =>
    param.field_id === 'repeat_config'
      ? { ...param, value: config as unknown as JsonValue }
      : param,
  ),
});

export const normalizeRepeatStructForInstance = (
  struct: NodeStruct,
  instanceId: string,
): NodeStruct => {
  if (!isRepeatNode(struct)) return struct;
  const current =
    getRepeatPseudoCycleConfig(struct) ??
    defaultRepeatPseudoCycleConfig(instanceId);
  return setRepeatPseudoCycleConfig(struct, {
    ...current,
    version: '2.0',
    semantic_id: `repeat:${instanceId}`,
    emit_mode: 'last_attempt',
    feedback_bindings: [],
    region: null,
    runtime_limits: null,
  });
};

const paramJsonValue = (param: NodeParam): JsonValue => {
  const value = param.value ?? param.default ?? null;
  return JSON.parse(JSON.stringify(value)) as JsonValue;
};

export type AttachRepeatSlotResult =
  | { ok: true; config: RepeatExecutionConfig }
  | { ok: false; reason: string };

export const attachRepeatSlot = (
  config: RepeatExecutionConfig,
  leaf: NodeStruct,
  role: RepeatSlotRole,
): AttachRepeatSlotResult => {
  if (role === 'attempt' && config.attempt) {
    return { ok: false, reason: 'Repeat already has its attempt call.' };
  }
  if (role === 'judge' && config.judge) {
    return { ok: false, reason: 'Repeat already has its judge call.' };
  }
  const eligible =
    role === 'attempt'
      ? isRepeatAttemptEligible(leaf)
      : isRepeatJudgeEligible(leaf);
  if (!eligible || !leaf.repeat_body_contract) {
    return {
      ok: false,
      reason: `This catalog node is not declared safe for the ${role} slot.`,
    };
  }

  const contract = leaf.repeat_body_contract;
  const staticParams: Record<string, JsonValue> = {};
  for (const fieldId of contract.static_params) {
    const param = leaf.params.find(
      (candidate) => candidate.field_id === fieldId,
    );
    if (!param) {
      return {
        ok: false,
        reason: `Body contract is missing static parameter ${fieldId}.`,
      };
    }
    staticParams[fieldId] = paramJsonValue(param);
  }

  const instanceId = config.semantic_id.replace(/^repeat:/, '');
  const semanticChildId = `repeat-${role}:${instanceId}`;
  const base = {
    kind: 'call' as const,
    semantic_child_id: semanticChildId,
    catalog_node_id: leaf.node_id,
    node_version: leaf.version,
    node_contract_version: leaf.contract_version!,
    capability_identity: leaf.capability_identity!,
    catalog_contract_digest: leaf.repeat_catalog_digest!,
    iteration_slot: null,
    static_params: staticParams,
    node_template: {
      ...(JSON.parse(JSON.stringify(leaf)) as Record<string, JsonValue>),
      react_flow_generated_id: semanticChildId,
    },
  };
  if (role === 'attempt') {
    const attempt: RepeatAttemptPin = {
      ...base,
      input_slot: contract.input_slots[0]
        ? { ...contract.input_slots[0] }
        : null,
    };
    return {
      ok: true,
      config: { ...config, attempt, runtime_limits: null },
    };
  }
  const judge: RepeatJudgePin = {
    ...base,
    subject_slot: { ...contract.subject_slots[0] },
  };
  return { ok: true, config: { ...config, judge, runtime_limits: null } };
};

export const detachRepeatSlot = (
  config: RepeatExecutionConfig,
  role: RepeatSlotRole,
): RepeatExecutionConfig =>
  role === 'attempt'
    ? {
        ...config,
        attempt: null,
        feedback_bindings: [],
        runtime_limits: null,
      }
    : { ...config, judge: null, runtime_limits: null };

// ── Constrained exit expression AST (structural mirror of the backend) ──

const COMPARISON_OPS = new Set(['eq', 'ne', 'lt', 'lte', 'gt', 'gte']);
const CONTEXT_ROOTS = new Set(['judge', 'attempt', 'iteration']);
const MAX_EXPRESSION_NODES = 128;
const MAX_EXPRESSION_DEPTH = 16;

export const validateExitExpression = (expression: unknown): string | null => {
  let nodeCount = 0;

  const visit = (node: unknown, depth: number): string | null => {
    nodeCount += 1;
    if (nodeCount > MAX_EXPRESSION_NODES) {
      return `Expression exceeds ${MAX_EXPRESSION_NODES} nodes.`;
    }
    if (depth > MAX_EXPRESSION_DEPTH) {
      return `Expression exceeds depth ${MAX_EXPRESSION_DEPTH}.`;
    }
    if (!isRecord(node) || typeof node.op !== 'string') {
      return 'Every expression node needs an "op" field.';
    }
    switch (node.op) {
      case 'literal':
        return 'value' in node ? null : 'literal requires a value.';
      case 'path': {
        if (!Array.isArray(node.path) || node.path.length === 0) {
          return 'path requires a non-empty segment array.';
        }
        const valid = node.path.every(
          (segment: unknown) =>
            (typeof segment === 'string' && segment.length > 0) ||
            (typeof segment === 'number' &&
              Number.isInteger(segment) &&
              segment >= 0),
        );
        if (!valid)
          return 'path segments must be keys or non-negative indexes.';
        const root = node.path[0];
        if (typeof root !== 'string' || !CONTEXT_ROOTS.has(root)) {
          return 'path must start with judge, attempt, or iteration.';
        }
        return null;
      }
      case 'not':
        return visit(node.arg, depth + 1);
      case 'is_null':
        return visit(node.arg, depth + 1);
      case 'and':
      case 'or': {
        if (!Array.isArray(node.args) || node.args.length === 0) {
          return `${node.op} requires a non-empty args array.`;
        }
        for (const arg of node.args) {
          const error = visit(arg, depth + 1);
          if (error) return error;
        }
        return null;
      }
      case 'in':
      case 'not_in':
        return visit(node.item, depth + 1) ?? visit(node.collection, depth + 1);
      case 'exists':
        return visit(node.path, depth + 1);
      default: {
        if (COMPARISON_OPS.has(node.op)) {
          return visit(node.left, depth + 1) ?? visit(node.right, depth + 1);
        }
        return `Unsupported expression op "${node.op}".`;
      }
    }
  };

  return visit(expression, 1);
};

export const parseFeedbackSourcePath = (
  raw: string,
): (string | number)[] | null => {
  const segments = raw
    .split('.')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
    .map((segment) => (/^\d+$/.test(segment) ? Number(segment) : segment));
  if (segments.length === 0) return null;
  const root = segments[0];
  if (typeof root !== 'string' || !CONTEXT_ROOTS.has(root)) return null;
  return segments;
};

export const feedbackSourcePathToString = (path: (string | number)[]): string =>
  path.map(String).join('.');

export const getRepeatFeedbackSlot = (
  struct: NodeStruct | undefined,
): RepeatBodySlot | null => {
  if (!struct || !isRepeatAttemptEligible(struct)) return null;
  return struct.repeat_body_contract?.feedback_slots[0] ?? null;
};

export const repeatFeedbackHandleId = (slot: RepeatBodySlot): string =>
  `${REPEAT_FEEDBACK_HANDLE_PREFIX}${slot.kind}:${slot.target_id}`;

export const parseRepeatFeedbackHandle = (
  handleId: string | null | undefined,
): RepeatBodySlot | null => {
  if (!handleId?.startsWith(REPEAT_FEEDBACK_HANDLE_PREFIX)) return null;
  const encoded = handleId.slice(REPEAT_FEEDBACK_HANDLE_PREFIX.length);
  const separator = encoded.indexOf(':');
  if (separator < 1 || separator === encoded.length - 1) return null;
  const kind = encoded.slice(0, separator);
  if (kind !== 'input' && kind !== 'param') return null;
  return { kind, target_id: encoded.slice(separator + 1) };
};

const expressionReferencesJudge = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.some(expressionReferencesJudge);
  if (!isRecord(value)) return false;
  if (
    value.op === 'path' &&
    Array.isArray(value.path) &&
    value.path[0] === 'judge'
  ) {
    return true;
  }
  return Object.values(value).some(expressionReferencesJudge);
};

export const validateRepeatPseudoCycleConfig = (
  config: RepeatPseudoCycleConfig | null,
  catalog?: NodeStruct[],
): string[] => {
  if (!config) return ['Repeat policy is missing or malformed.'];
  const carrier = catalog?.find(
    (candidate) => candidate.node_id === REPEAT_NODE_ID,
  );
  const maxIterations =
    carrier?.repeat_runtime_limits?.max_iterations ??
    REPEAT_MAX_ITERATIONS_HARD;
  const errors: string[] = [];
  if (
    !Number.isInteger(config.max_iterations) ||
    config.max_iterations < 1 ||
    config.max_iterations > Math.min(maxIterations, REPEAT_MAX_ITERATIONS_HARD)
  ) {
    errors.push(
      `Iteration cap must be between 1 and ${Math.min(
        maxIterations,
        REPEAT_MAX_ITERATIONS_HARD,
      )}.`,
    );
  }
  const expressionError = validateExitExpression(config.exit_condition);
  if (expressionError) errors.push(`Exit condition: ${expressionError}`);
  if (!expressionReferencesJudge(config.exit_condition)) {
    errors.push(
      'Exit condition must read a downstream evaluator value (judge.*).',
    );
  }
  if (
    config.feedback_source_path.length < 2 ||
    config.feedback_source_path[0] !== 'judge'
  ) {
    errors.push('Feedback must use a downstream evaluator path (judge.*).');
  }
  if (
    config.region != null ||
    config.runtime_limits != null ||
    config.feedback_bindings.length > 0
  ) {
    errors.push('Repeat policy contains server-owned compiled state.');
  }
  return errors;
};

export const validateRepeatConfig = (
  config: RepeatExecutionConfig | null,
  catalog?: NodeStruct[],
): string[] => {
  if (!config) return ['Repeat execution metadata is missing or malformed.'];
  const errors: string[] = [];
  const carrier = catalog?.find(
    (candidate) => candidate.node_id === REPEAT_NODE_ID,
  );
  const maxIterations =
    carrier?.repeat_runtime_limits?.max_iterations ??
    config.runtime_limits?.max_iterations ??
    REPEAT_MAX_ITERATIONS_HARD;
  if (
    !Number.isInteger(config.max_iterations) ||
    config.max_iterations < 1 ||
    config.max_iterations > Math.min(maxIterations, REPEAT_MAX_ITERATIONS_HARD)
  ) {
    errors.push(
      `Max iterations must be between 1 and ${Math.min(
        maxIterations,
        REPEAT_MAX_ITERATIONS_HARD,
      )}.`,
    );
  }
  if (!config.attempt) {
    errors.push('Pin exactly one supported attempt call.');
  }
  if (!config.judge) {
    errors.push('Pin exactly one supported judge call.');
  }
  if (!config.exit_condition) {
    errors.push('Define the exit condition expression.');
  } else {
    const expressionError = validateExitExpression(config.exit_condition);
    if (expressionError) errors.push(`Exit condition: ${expressionError}`);
  }

  const attemptCatalog = catalog?.find(
    (candidate) =>
      candidate.node_id === config.attempt?.catalog_node_id &&
      candidate.version === config.attempt?.node_version,
  );
  const judgeCatalog = catalog?.find(
    (candidate) =>
      candidate.node_id === config.judge?.catalog_node_id &&
      candidate.version === config.judge?.node_version,
  );
  if (catalog && config.attempt) {
    if (!attemptCatalog || !isRepeatAttemptEligible(attemptCatalog)) {
      errors.push(
        'The pinned attempt is unavailable or no longer Repeat-safe.',
      );
    } else if (
      attemptCatalog.contract_version !==
        config.attempt.node_contract_version ||
      attemptCatalog.capability_identity !==
        config.attempt.capability_identity ||
      attemptCatalog.repeat_catalog_digest !==
        config.attempt.catalog_contract_digest
    ) {
      errors.push('The pinned attempt is stale; remove it and pin it again.');
    }
  }
  if (catalog && config.judge) {
    if (!judgeCatalog || !isRepeatJudgeEligible(judgeCatalog)) {
      errors.push('The pinned judge is unavailable or no longer Repeat-safe.');
    } else if (
      judgeCatalog.contract_version !== config.judge.node_contract_version ||
      judgeCatalog.capability_identity !== config.judge.capability_identity ||
      judgeCatalog.repeat_catalog_digest !==
        config.judge.catalog_contract_digest
    ) {
      errors.push('The pinned judge is stale; remove it and pin it again.');
    }
  }

  const declaredFeedbackSlots =
    attemptCatalog?.repeat_body_contract?.feedback_slots ?? null;
  const seenTargets = new Set<string>();
  const reserved = new Set<string>();
  if (config.attempt?.input_slot) {
    reserved.add(
      `${config.attempt.input_slot.kind}:${config.attempt.input_slot.target_id}`,
    );
  }
  if (config.attempt?.iteration_slot) {
    reserved.add(
      `${config.attempt.iteration_slot.kind}:${config.attempt.iteration_slot.target_id}`,
    );
  }
  config.feedback_bindings.forEach((binding, index) => {
    const label = `Feedback binding ${index + 1}`;
    if (
      !Array.isArray(binding.source_path) ||
      binding.source_path.length === 0 ||
      typeof binding.source_path[0] !== 'string' ||
      !CONTEXT_ROOTS.has(binding.source_path[0])
    ) {
      errors.push(
        `${label}: source path must start with judge, attempt, or iteration.`,
      );
    }
    const key = `${binding.target.kind}:${binding.target.target_id}`;
    if (seenTargets.has(key)) {
      errors.push(`${label}: duplicate target slot.`);
    }
    seenTargets.add(key);
    if (reserved.has(key)) {
      errors.push(`${label}: target collides with the attempt input slot.`);
    }
    if (
      declaredFeedbackSlots !== null &&
      !declaredFeedbackSlots.some(
        (slot) =>
          slot.kind === binding.target.kind &&
          slot.target_id === binding.target.target_id,
      )
    ) {
      errors.push(`${label}: target is not a declared attempt feedback slot.`);
    }
  });
  return errors;
};

export const parseRepeatControlResult = (
  value: unknown,
): RepeatControlResult | null => {
  let candidate = value;
  if (isRecord(candidate) && isRecord(candidate.data))
    candidate = candidate.data;
  if (!isRecord(candidate)) return null;
  if (
    !Array.isArray(candidate.iterations) ||
    typeof candidate.satisfied !== 'boolean' ||
    (candidate.exit_reason !== 'satisfied' &&
      candidate.exit_reason !== 'max_iterations_exhausted') ||
    typeof candidate.iteration_count !== 'number' ||
    typeof candidate.digest !== 'string' ||
    !isRecord(candidate.provenance)
  ) {
    return null;
  }
  return candidate as unknown as RepeatControlResult;
};

export const latestRepeatProgress = (
  events: NodeStatusEvent[] | undefined,
): RepeatProgress | null => {
  if (!events) return null;
  for (let index = events.length - 1; index >= 0; index--) {
    const data = (events[index] as unknown as { data?: unknown }).data;
    if (!isRecord(data) || !isRecord(data.repeat_control)) continue;
    const progress = data.repeat_control;
    if (
      typeof progress.iteration === 'number' &&
      typeof progress.max_iterations === 'number'
    ) {
      return progress as unknown as RepeatProgress;
    }
  }
  return null;
};
